import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Paper,
  Fade,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  RocketLaunch as RocketLaunchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  sendMessage,
  sendMessageStreaming,
  getChatHistory,
  getChatSessions,
  createNewSessionId,
  renameChatSession,
  deleteChatSession,
} from '../../services/chatApi';
import { getAvailableAgents, getCurrentAgent, assignAgent } from '../../services/agentApi';
import ExecutionTraceDrawer from '../common/ExecutionTraceDrawer';

const mapHistoryToUiMessages = (apiMessages) =>
  apiMessages
    .filter((m) => m?.content?.trim())
    .map((m, index) => ({
      id: `history-${index}-${m.created_at || index}`,
      text: m.content,
      sender: m.role === 'user' ? 'user' : 'bot',
      timestamp: m.created_at ? new Date(m.created_at) : new Date(),
      requestId: m.role !== 'user' && m.request_id ? m.request_id : null,
    }));

const getDefaultWelcomeMessages = () => [
  {
    id: 1,
    text: "Hello! I'm your AI assistant. How can I help you today?",
    sender: 'bot',
    timestamp: new Date(),
  },
];

const ChatHub = () => {
  const [messages, setMessages] = useState(getDefaultWelcomeMessages);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [traceDrawerOpen, setTraceDrawerOpen] = useState(false);
  const [traceRequestId, setTraceRequestId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatSessions, setChatSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState(null);
  const [sessionMenuTarget, setSessionMenuTarget] = useState(null);
  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const renameInputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const loadChatHistory = async (sessionId, agent) => {
    if (!sessionId) return;
    const isLangchain = agent?.agentAppName?.toLowerCase() === 'langchain';
    if (agent && !isLangchain) return;
    try {
      const rows = await getChatHistory(sessionId);
      if (rows?.length > 0) {
        setMessages(mapHistoryToUiMessages(rows));
      } else {
        setMessages(getDefaultWelcomeMessages());
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadChatSessions = async (agent) => {
    if (!agent) return;
    const isLangchain = agent?.agentAppName?.toLowerCase() === 'langchain';
    if (!isLangchain) return;
    setSessionsLoading(true);
    try {
      const sessions = await getChatSessions(50);
      setChatSessions(sessions);
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Load agents and restore chat history on mount
  useEffect(() => {
    const init = async () => {
      let agent = null;
      try {
        const [agents, current] = await Promise.all([
          getAvailableAgents(),
          getCurrentAgent().catch(() => null) // Ignore error if no assignment
        ]);
        setAvailableAgents(agents || []);
        setCurrentAgent(current);
        agent = current;
      } catch (error) {
        console.error('Error loading agents:', error);
      }
      const sessionId = localStorage.getItem('n8n_chat_sessionId');
      await loadChatHistory(sessionId, agent);
      await loadChatSessions(agent);
    };
    init();
  }, []);

  const runStreamingTurn = async (displayText) => {
    if (isLoading || !displayText?.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: displayText,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    setIsLoading(true);
    const botId = Date.now() + 1;
    setMessages((prev) => [
      ...prev,
      {
        id: botId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        requestId: null,
      },
    ]);

    try {
      await sendMessageStreaming(displayText, {
        onChunk: (delta) => {
          flushSync(() => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === botId ? { ...m, text: m.text + delta } : m
              )
            );
          });
        },
        onReplace: (text) => {
          flushSync(() => {
            setMessages((prev) =>
              prev.map((m) => (m.id === botId ? { ...m, text: text || '' } : m))
            );
          });
        },
        onDone: ({ requestId, execution_time_ms: execMs }) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === botId
                ? {
                    ...m,
                    requestId: requestId || m.requestId,
                    executionTimeMs: execMs,
                  }
                : m
            )
          );
        },
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId
            ? {
                ...m,
                text: error.message || 'Sorry, I encountered an error. Please try again later.',
                isError: true,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const text = inputMessage.trim();
    setInputMessage('');

    const isLangchain = currentAgent?.agentAppName?.toLowerCase() === 'langchain';
    if (isLangchain) {
      await runStreamingTurn(text);
      return;
    }

    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    const botId = Date.now() + 1;
    setMessages((prev) => [
      ...prev,
      {
        id: botId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        requestId: null,
      },
    ]);

    try {
      const response = await sendMessage(text);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId
            ? {
                ...m,
                text: response.message || 'No response received',
                requestId: response.requestId || response.request_id || m.requestId,
              }
            : m
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === botId
            ? {
                ...m,
                text: error.message || 'Sorry, I encountered an error. Please try again later.',
                isError: true,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopy = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderMessageText = (text) => {
    if (!text) return '';
    // Support lightweight markdown bold syntax: **text**
    const parts = String(text).split(/(\*\*.+?\*\*)/g);
    return parts.map((part, index) => {
      const isBold = part.startsWith('**') && part.endsWith('**') && part.length > 4;
      if (!isBold) return <React.Fragment key={index}>{part}</React.Fragment>;
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    });
  };

  const handleAgentMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAgentMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAgentSelect = async (agent) => {
    try {
      await assignAgent(agent.tenantAgentId);
      const selectedAgent = {
        ...agent,
        agentName: agent.agentName,
        tenantAgentId: agent.tenantAgentId,
        agentAppName: agent.agentAppName, // Include agentAppName to determine streaming behavior
      };
      setCurrentAgent(selectedAgent);
      handleAgentMenuClose();

      const sessionId = localStorage.getItem('n8n_chat_sessionId');
      await loadChatSessions(selectedAgent);
      await loadChatHistory(sessionId, selectedAgent);
    } catch (error) {
      console.error('Error assigning agent:', error);
    }
  };

  const handleNewChat = () => {
    if (isLoading) return;
    createNewSessionId();
    setMessages(getDefaultWelcomeMessages());
    setInputMessage('');
    setCopiedId(null);
    setTraceDrawerOpen(false);
    setTraceRequestId(null);
    inputRef.current?.focus();
  };

  const openRunTrace = (requestId) => {
    if (!requestId) return;
    setTraceRequestId(requestId);
    setTraceDrawerOpen(true);
  };

  const handleSessionClick = async (threadId) => {
    if (isLoading || !threadId || renamingSessionId) return;
    localStorage.setItem('n8n_chat_sessionId', threadId);
    await loadChatHistory(threadId, currentAgent);
  };

  const handleSessionMenuOpen = (event, session) => {
    event.stopPropagation();
    if (isLoading) return;
    setSessionMenuAnchor(event.currentTarget);
    setSessionMenuTarget(session);
  };

  const handleSessionMenuClose = () => {
    setSessionMenuAnchor(null);
    setSessionMenuTarget(null);
  };

  const startRenameSession = (session) => {
    setRenamingSessionId(session.threadId);
    setRenameDraft(session.title || 'New conversation');
    setRenameError('');
    handleSessionMenuClose();
    setTimeout(() => renameInputRef.current?.focus(), 0);
  };

  const cancelRenameSession = () => {
    setRenamingSessionId(null);
    setRenameDraft('');
    setRenameError('');
    setRenameLoading(false);
  };

  const submitRenameSession = async (threadId) => {
    const trimmed = renameDraft.trim();
    if (!trimmed || !threadId) {
      setRenameError('Title cannot be empty');
      return;
    }
    setRenameLoading(true);
    setRenameError('');
    try {
      const result = await renameChatSession(threadId, trimmed);
      setChatSessions((prev) =>
        prev.map((s) =>
          s.threadId === threadId ? { ...s, title: result.title } : s
        )
      );
      cancelRenameSession();
    } catch (error) {
      setRenameError(error.message || 'Failed to rename chat');
    } finally {
      setRenameLoading(false);
    }
  };

  const openDeleteSessionDialog = (session) => {
    setSessionToDelete(session);
    setDeleteError('');
    setDeleteDialogOpen(true);
    handleSessionMenuClose();
  };

  const closeDeleteSessionDialog = (force = false) => {
    if (deleteLoading && !force) return;
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
    setDeleteError('');
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete?.threadId) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteChatSession(sessionToDelete.threadId);
      setChatSessions((prev) =>
        prev.filter((s) => s.threadId !== sessionToDelete.threadId)
      );
      const activeId = localStorage.getItem('n8n_chat_sessionId');
      if (activeId === sessionToDelete.threadId) {
        handleNewChat();
      }
      closeDeleteSessionDialog(true);
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete chat');
    } finally {
      setDeleteLoading(false);
    }
  };

  const activeThreadId = localStorage.getItem('n8n_chat_sessionId');

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: '#f7f7f8',
        position: 'relative',
        p: 0,
        overflow: 'hidden', // Prevent page scrolling
        margin: '-24px', // Offset parent padding (p: 3 = 24px)
        width: 'calc(100% + 48px)', // Compensate for negative margin
      }}
    >
      <Box
        sx={{
          width: sidebarOpen ? 260 : 0,
          flexShrink: 0,
          overflow: 'hidden',
          borderRight: sidebarOpen ? '1px solid #e5e7eb' : 'none',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={handleNewChat}
            disabled={isLoading}
            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
          >
            New Chat
          </Button>
          <IconButton size="small" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {sessionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: '#6B0F2A' }} />
            </Box>
          ) : chatSessions.length === 0 ? (
            <Typography variant="body2" sx={{ px: 2, py: 1, color: '#8e8ea0' }}>
              No previous chats
            </Typography>
          ) : (
            chatSessions.map((session) => {
              const isActive = session.threadId === activeThreadId;
              return (
              <Box
                key={session.threadId}
                onClick={() => handleSessionClick(session.threadId)}
                sx={{
                  px: 1,
                  py: 0.375,
                  mx: 1,
                  mb: 0,
                  borderRadius: 1,
                  cursor: isLoading ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ...(isActive && {
                    backgroundColor: '#e8e8ea',
                    boxShadow: 'inset 3px 0 0 #6B0F2A',
                  }),
                  '&:hover': {
                    backgroundColor: isActive ? '#e8e8ea' : '#e5e7eb',
                  },
                }}
              >
                {renamingSessionId === session.threadId ? (
                  <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                    <TextField
                      inputRef={renameInputRef}
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      size="small"
                      fullWidth
                      disabled={renameLoading}
                      error={Boolean(renameError)}
                      helperText={renameError || ' '}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          submitRenameSession(session.threadId);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelRenameSession();
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-input': { py: 0.75, fontSize: '0.875rem' },
                        '& .MuiFormHelperText-root': { mx: 0, mt: 0.25 },
                      }}
                    />
                    {renameLoading && (
                      <CircularProgress size={14} sx={{ color: '#6B0F2A', mt: 0.5 }} />
                    )}
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" noWrap sx={{ color: '#353740', flex: 1, minWidth: 0 }}>
                      {session.title || 'New conversation'}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Session options"
                      disabled={isLoading}
                      onClick={(e) => handleSessionMenuOpen(e, session)}
                      sx={{ flexShrink: 0, opacity: 0.7, '&:hover': { opacity: 1 } }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
              );
            })
          )}
        </Box>
        <Menu
          anchorEl={sessionMenuAnchor}
          open={Boolean(sessionMenuAnchor)}
          onClose={handleSessionMenuClose}
        >
          <MenuItem
            onClick={() => startRenameSession(sessionMenuTarget)}
            disabled={!sessionMenuTarget}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => openDeleteSessionDialog(sessionMenuTarget)}
            disabled={!sessionMenuTarget}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        </Menu>
        <Dialog open={deleteDialogOpen} onClose={closeDeleteSessionDialog}>
          <DialogTitle>Delete chat?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {`This will permanently delete "${sessionToDelete?.title || 'New conversation'}" and its message history. This action cannot be undone.`}
            </Typography>
            {deleteError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {deleteError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteSessionDialog} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteSession}
              color="error"
              variant="contained"
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Main Card Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          position: 'relative', // Needed for absolute positioning of input
          p: 2,
          minWidth: 0,
        }}
      >
        {!sidebarOpen && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <IconButton size="small" onClick={() => setSidebarOpen(true)} aria-label="Open sidebar">
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {/* Messages Container */}
        <Box
          ref={messagesContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, sm: 4, md: 6 },
            py: 4,
            pb: 20, // Add padding bottom so messages don't get hidden behind fixed input
            backgroundColor: 'transparent',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          {messages.map((message, index) => (
            <Fade in={true} key={message.id} timeout={300}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 4,
                  flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  maxWidth: '768px',
                  margin: message.sender === 'user' ? '0 0 0 auto' : '0 auto 0 0',
                }}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    bgcolor: message.sender === 'user' ? '#6B0F2A' : '#6B0F2A',
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                  }}
                >
                  {message.sender === 'user' ? (
                    <PersonIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <BotIcon sx={{ fontSize: 18 }} />
                  )}
                </Avatar>

                {/* Message Content */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.5,
                      backgroundColor: message.sender === 'user' ? '#f0f0f0' : '#f4e6f7',
                      borderRadius: 3,
                      border: 'none',
                      maxWidth: '100%',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                      '&:hover .copy-button': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: message.sender === 'user' ? '#1f1f1f' : '#353740',
                        lineHeight: 1.75,
                        fontSize: '0.9375rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        flex: 1,
                        display: 'block',
                      }}
                    >
                      {message.text ? renderMessageText(message.text) : (message.sender === 'bot' && isLoading ? (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            gap: 0.5,
                            alignItems: 'center',
                            '& .dot': {
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: '#8e8ea0',
                              animation: 'pulse 1.4s ease-in-out infinite',
                              '&:nth-of-type(1)': {
                                animationDelay: '0s',
                              },
                              '&:nth-of-type(2)': {
                                animationDelay: '0.2s',
                              },
                              '&:nth-of-type(3)': {
                                animationDelay: '0.4s',
                              },
                            },
                            '@keyframes pulse': {
                              '0%, 60%, 100%': {
                                opacity: 0.3,
                                transform: 'scale(0.8)',
                              },
                              '30%': {
                                opacity: 1,
                                transform: 'scale(1)',
                              },
                            },
                          }}
                        >
                          <Box className="dot" />
                          <Box className="dot" />
                          <Box className="dot" />
                        </Box>
                      ) : '')}
                    </Typography>

                    {/* Copy + Run trace */}
                    {message.sender === 'bot' && message.text && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
                        <IconButton
                          className="copy-button"
                          size="small"
                          onClick={() => handleCopy(message.text, message.id)}
                          sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            width: 24,
                            height: 24,
                            color: '#8e8ea0',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                              color: '#353740',
                            },
                          }}
                        >
                          {copiedId === message.id ? (
                            <CheckIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <CopyIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                        {message.requestId && (
                          <Tooltip title="Run trace">
                            <IconButton
                              className="copy-button"
                              size="small"
                              onClick={() => openRunTrace(message.requestId)}
                              sx={{
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                width: 24,
                                height: 24,
                                color: '#8e8ea0',
                                '&:hover': {
                                  backgroundColor: 'rgba(107, 15, 42, 0.08)',
                                  color: '#6B0F2A',
                                },
                              }}
                            >
                              <TimelineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Paper>

                  {/* Timestamp and streaming time */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#8e8ea0',
                      fontSize: '0.75rem',
                      mt: 0.5,
                    }}
                  >
                    {formatTime(message.timestamp)}
                    {message.sender === 'bot' &&
                      message.executionTimeMs != null &&
                      Number.isFinite(message.executionTimeMs) && (
                        <span> · {((message.executionTimeMs || 0) / 1000).toFixed(1)}s</span>
                      )}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          ))}

          {/* Loading indicator — hide when streaming: we already show placeholder bot bubble with "..." */}
          {isLoading &&
            !(messages.length >= 2 && messages[messages.length - 1].sender === 'bot') && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 4,
                alignItems: 'flex-start',
                maxWidth: '768px',
                margin: '0 auto 0 0',
              }}
            >
              <Avatar
                sx={{
                  bgcolor: '#6B0F2A',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                }}
              >
                <BotIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#8e8ea0',
                }}
              >
                <CircularProgress size={16} sx={{ color: '#6B0F2A' }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  Thinking...
                </Typography>
              </Box>
            </Box>
            )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area - Fixed at bottom */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0) 100%)',
            py: 3,
            px: { xs: 2, sm: 4, md: 6 },
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              maxWidth: '768px',
              margin: '0 auto',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #d1d5db',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'flex-end',
                padding: '6px 6px 6px 14px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'border-color 0.2s',
                gap: 1,
                '&:focus-within': {
                  borderColor: '#6B0F2A',
                  boxShadow: '0 0 0 3px rgba(107, 15, 42, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={6}
                placeholder="Message AI Assistant..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.9375rem',
                    fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '&::placeholder': {
                      color: '#9ca3af',
                    },
                  },
                }}
              />

              {/* Agent Selector Dropdown */}
              <Box
                onClick={handleAgentMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  borderLeft: '1px solid #e5e7eb',
                  pl: 1.5,
                  ml: 0.5,
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                  },
                }}
              >
                <PsychologyIcon sx={{ color: '#6B0F2A', fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                  {currentAgent?.agentName || 'System Default'}
                </Typography>
                <ArrowDropDownIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              </Box>

              {/* Agent Dropdown Menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleAgentMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }
                }}
              >
                {availableAgents.length > 0 ? (
                  availableAgents.map((agent) => (
                    <MenuItem
                      key={agent.tenantAgentId}
                      onClick={() => handleAgentSelect(agent)}
                      selected={currentAgent?.tenantAgentId === agent.tenantAgentId}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: '#f0e7f3',
                        },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {agent.agentName}
                        </Typography>
                        {agent.description && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {agent.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No agents configured
                    </Typography>
                  </MenuItem>
                )}
              </Menu>

              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                sx={{
                  bgcolor: 'transparent',
                  color: inputMessage.trim() && !isLoading ? '#6B0F2A' : '#9ca3af',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  transition: 'all 0.2s',
                  ml: 0.5,
                  '&:hover': {
                    bgcolor: 'transparent',
                    '& svg': {
                      transform: 'scale(1.15)',
                    },
                  },
                  '&:disabled': {
                    bgcolor: 'transparent',
                    color: '#9ca3af',
                  },
                  '& svg': {
                    transition: 'transform 0.2s, color 0.2s',
                  },
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Paper>
          </Box>
        </Box>

        <ExecutionTraceDrawer
          open={traceDrawerOpen}
          onClose={() => {
            setTraceDrawerOpen(false);
            setTraceRequestId(null);
          }}
          requestId={traceRequestId}
          title="Run trace"
        />
      </Box>
    </Box>
  );
};

export default ChatHub;
